import {
  MdBorderClear,
  MdBorderOuter,
  MdGrid4X4,
  MdOutlineBorderAll,
  MdOutlineBorderHorizontal,
} from "react-icons/md";
import {
  Col,
  Form,
  Input,
  Select,
  Switch,
  Segmented,
  Slider,
  Row,
  Divider,
  Typography,
} from "antd";
import ColorPicker from "components/ColorPicker/ColorPicker";
import UploadInput from "components/UploadInput";
import { useTranslation } from "react-i18next";
import { FormInstance } from "antd";
import React from "react";
import { TextEditor } from "@neonumy/ant-design";

interface FontOption {
  label: unknown;
  value: unknown;
}

interface TemplateSettingsFormFormPropsType {
  form: FormInstance;
  newFontsOptions: FontOption[];
  onDeleteLogo?: () => void;
  onDeleteBackgroundImage?: () => void;
}

export default function NewTemplateSettingsForm(
  props: TemplateSettingsFormFormPropsType,
): React.ReactElement {
  const { newFontsOptions, onDeleteLogo, onDeleteBackgroundImage } = props;

  const { t } = useTranslation();

  return (
    <Col xs={24} sm={24}>
      {/* Logo and Branding Section */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <Form.Item name="logo" label={t("Company logo")}>
            <UploadInput alt="logo" onDelete={onDeleteLogo} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Form.Item name="backgroundImage" label={t("Template watermark")}>
            <UploadInput alt="backgroundImage" onDelete={onDeleteBackgroundImage} />
          </Form.Item>
        </Col>
      </Row>
      {/* Watermark and Typography Section */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <Form.Item name="logoPosition" label={t("Logo position")}>
            <Segmented
              size="small"
              defaultValue="left"
              className="modern-segmented-control"
              options={[
                {
                  label: (
                    <div className="segmented-option-content">
                      <span className="segmented-option-text">{t("Left")}</span>
                    </div>
                  ),
                  value: "left",
                },
                {
                  label: (
                    <div className="segmented-option-content">
                      <span className="segmented-option-text">{t("Right")}</span>
                    </div>
                  ),
                  value: "right",
                },
              ]}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={12}>
          <Form.Item name="logoDescription" label={t("Logo description")}>
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Form.Item name="backgroundImageOpacity" label={t("Watermark opacity")}>
            <Slider min={0.01} max={0.5} step={0.01} />
          </Form.Item>
        </Col>
      </Row>

      {/* Language and Colors Section */}
      <Row gutter={[16, 16]}>
        <Col xs={12} sm={8} md={6}>
          <Form.Item name="baseColor" label={t("Base Color")}>
            <ColorPicker />
          </Form.Item>
        </Col>
        <Col xs={12} sm={8} md={6}>
          <Form.Item name="titleColor" label={t("Title")}>
            <ColorPicker />
          </Form.Item>
        </Col>
        <Col xs={12} sm={8} md={6}>
          <Form.Item name="baseTextColor" label={t("Text Color")}>
            <ColorPicker />
          </Form.Item>
        </Col>
        <Col xs={12} sm={8} md={6}>
          <Form.Item name="baseTextColorBackground" label={t("Table header color")}>
            <ColorPicker />
          </Form.Item>
        </Col>
      </Row>

      {/* Table Settings and Document Options */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={24} md={12}>
          <Form.Item name="tableBorder" label={t("Table borders")}>
            <Segmented
              size="small"
              defaultValue="border_none"
              className="modern-segmented-control"
              options={[
                {
                  label: (
                    <div className="segmented-option-content">
                      <MdOutlineBorderAll className="segmented-option-icon" />
                      <span className="segmented-option-text">{t("All")}</span>
                    </div>
                  ),
                  value: "border_all",
                },
                {
                  label: (
                    <div className="segmented-option-content">
                      <MdBorderOuter className="segmented-option-icon" />
                      <span className="segmented-option-text">{t("Outer")}</span>
                    </div>
                  ),
                  value: "border_outer",
                },
                {
                  label: (
                    <div className="segmented-option-content">
                      <MdGrid4X4 className="segmented-option-icon" />
                      <span className="segmented-option-text">{t("Grid")}</span>
                    </div>
                  ),
                  value: "border_columns",
                },
                {
                  label: (
                    <div className="segmented-option-content">
                      <MdOutlineBorderHorizontal className="segmented-option-icon" />
                      <span className="segmented-option-text">{t("Row")}</span>
                    </div>
                  ),
                  value: "border_row",
                },
                {
                  label: (
                    <div className="segmented-option-content">
                      <MdBorderClear className="segmented-option-icon" />
                      <span className="segmented-option-text">{t("None")}</span>
                    </div>
                  ),
                  value: "border_none",
                },
              ]}
            />
          </Form.Item>
        </Col>
      </Row>

      {/* Notes Section */}
      <Row gutter={[16, 16]}>
        <Col xs={12} sm={6} md={6}>
          <Form.Item
            label={t("Description on document")}
            name="showDocumentDescription"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Col>
        <Col xs={12} sm={6} md={6}>
          <Form.Item
            label={t("Product name on line")}
            name="productNameOnLine"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Form.Item name="font" label={t("Fonts")}>
            <Select options={newFontsOptions} placeholder={t("Select a font")} showSearch />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Form.Item name="templateLanguage" label={t("Template language")}>
            <Select
              options={[
                { label: t("French"), value: "fr" },
                { label: t("Spanish"), value: "es" },
                { label: t("Portuguese"), value: "pt" },
              ]}
            />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item
            name="note"
            label={t("Notes")}
            extra={t("This note will auto-fill the note field when creating invoices")}
          >
            <TextEditor allowImageUpload={false} showSelectionTooltip={true} />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item
            name="latePaymentPenalty"
            label={
              <>
                {t("Late payment penalty")}
                <Typography.Text type="secondary" style={{ marginLeft: 4 }}>
                  ({t("mandatory for commercial invoices since 2013")})
                </Typography.Text>
              </>
            }
            rules={[{ required: true, message: t("This field is required") }]}
            extra={
              <Typography.Text
                type="secondary"
                copyable={{
                  text: "En cas de retard de paiement, des pénalités calculées au taux légal seront exigibles.",
                }}
                style={{ marginBottom: 0 }}
              >
                {t("Use the example")}: En cas de retard de paiement, des pénalités calculées au
                taux légal seront exigibles.
              </Typography.Text>
            }
          >
            <TextEditor allowImageUpload={false} showSelectionTooltip={true} />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item
            name="fixedRecoveryFee"
            label={
              <>
                {t("Fixed recovery fee")}
                <Typography.Text type="secondary" style={{ marginLeft: 4 }}>
                  ({t("compensation mandatory on commercial invoices since 2013")})
                </Typography.Text>
              </>
            }
            rules={[{ required: true, message: t("This field is required") }]}
            extra={
              <Typography.Text
                type="secondary"
                copyable={{
                  text: "Indemnité forfaitaire pour frais de recouvrement due au créancier de 40 euros.",
                }}
                style={{ marginBottom: 0 }}
              >
                {t("Use the example")}: Indemnité forfaitaire pour frais de recouvrement due au
                créancier de 40 euros.
              </Typography.Text>
            }
          >
            <TextEditor allowImageUpload={false} showSelectionTooltip={true} />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item
            name="discount"
            label={
              <>
                {t("Escompte")}
                <Typography.Text type="secondary" style={{ marginLeft: 4 }}>
                  ({t("early payment discount terms")})
                </Typography.Text>
              </>
            }
            rules={[{ required: true, message: t("This field is required") }]}
            extra={
              <Typography.Text
                type="secondary"
                copyable={{
                  text: "Aucun escompte accordé pour paiement anticipé.",
                }}
                style={{ marginBottom: 0 }}
              >
                {t("Use the example")}: Aucun escompte accordé pour paiement anticipé.
              </Typography.Text>
            }
          >
            <TextEditor allowImageUpload={false} showSelectionTooltip={true} />
          </Form.Item>
        </Col>
      </Row>
      <Divider orientation="left" plain style={{ margin: "16px 0" }}>
        <span>{t("These settings are for preview purposes only")}</span>
      </Divider>
      {/* Document Visibility Controls */}
      <Row gutter={[16, 16]}>
        <Col xs={12} sm={3} md={3}>
          <Form.Item label={t("Name")} name="showName" valuePropName="checked">
            <Switch size="small" />
          </Form.Item>
        </Col>
        <Col xs={12} sm={4} md={4}>
          <Form.Item label={t("Description")} name="showDescription" valuePropName="checked">
            <Switch size="small" />
          </Form.Item>
        </Col>
        <Col xs={12} sm={4} md={4}>
          <Form.Item label={t("Stamp section")} name="showStampSpace" valuePropName="checked">
            <Switch size="small" />
          </Form.Item>
        </Col>
        <Col xs={12} sm={5} md={5}>
          <Form.Item
            label={t("Delivery address")}
            name="showDeliveryAddress"
            valuePropName="checked"
          >
            <Switch size="small" />
          </Form.Item>
        </Col>
        <Col xs={12} sm={8} md={8}>
          <Form.Item
            label={t("Mention and legal status")}
            name="showMentionAndLegalStatus"
            valuePropName="checked"
          >
            <Switch size="small" />
          </Form.Item>
          <Form.Item name="template" hidden>
            <Input />
          </Form.Item>
        </Col>
      </Row>
    </Col>
  );
}
